/**
 * The conversation itself — his messages plain and right-aligned, hers
 * arriving word by word in her colour. By default nothing is kept: the
 * transcript lives in component state and dies with the sheet. She remembers
 * *facts* (the close call distils them server-side); the words themselves
 * are gone, which is the correct physics for talking to a ghost.
 *
 * A parent can opt into same-day continuity instead — `initialHistory` seeds
 * today's earlier turns as plain static text (no reveal; that animation is
 * reserved for a line arriving live), and `onHistoryChange` reports the
 * transcript as it grows so the parent can cache it. See `chatDay.ts` for
 * the Dexie-backed cache and the transport wrapper that makes reopening
 * free of a redundant server-side extraction.
 *
 * The thread does not render its own mark — the zone's big mark is her body,
 * and this component only reports what the body should be doing (`onPose`):
 * thinking while the model works, listening while the mic is open. The send
 * itself is a tap, so `watchTaps` already turns her eyes toward the button.
 *
 * Voice input rides the same two engines the voice logger uses: on-device
 * Lithuanian speech where the platform has it, and a recorded clip sent for
 * server-side transcription where it does not (iOS standalone). A clip turn
 * renders as „(balso žinutė)" until the reply brings back what she heard.
 */

import { useEffect, useRef, useState } from 'react'
import { playCue } from '../sound/engine.js'
import { GhostReveal } from '../stage/GhostReveal.js'
import { recorderAvailable, useRecorder, type VoiceClip } from '../voice/useRecorder.js'
import { onDeviceSpeech, useSpeech } from '../voice/useSpeech.js'
import {
  apiChatTransport,
  type ChatSend,
  type ChatTransport,
  type ChatTurn,
} from './chatTransport.js'
import { recordRudeExit } from './interaction.js'

/** The server caps history at 14 turns; older ones simply age out of context. */
const HISTORY_MAX = 14
/** His turn cap mirrors the server's schema. */
const MESSAGE_MAX = 500
/** The live-chat endpoint's `turn` field caps here too (`chat.ts`). */
const TURN_MAX = 40

export type ChatPose = 'thinking' | 'listening' | null

export interface ChatThreadProps {
  transport?: ChatTransport
  /** What her mark should be doing right now; the zone owns the mark. */
  onPose?: (pose: ChatPose) => void
  /**
   * A personal callback the zone drew before mounting this thread — her
   * first word, spoken before he says anything. Rendered once as a `her`
   * bubble and folded into `record` ahead of any real turn, so both the
   * live model call and the close-time extraction see that she opened.
   * Read only at mount: the caller computes it before rendering the thread
   * (see `HerZone`), so a later change has nothing left to seed.
   * Null/undefined when there was nothing personal to open with — the
   * thread then starts exactly as it always has, with no opening bubble.
   */
  opener?: string | null
  /**
   * Turns already said today, from an earlier mount of this thread (see
   * `chatDay.ts`) — rendered ahead of anything said in this mount as plain
   * static text, and used to seed `record`, the bubble-key counter, and the
   * turn-index counter so a resumed conversation continues rather than
   * restarting. Read only at mount, same as `opener`.
   */
  initialHistory?: readonly ChatTurn[]
  /**
   * Fires with the full transcript right after `record` actually changes,
   * so a parent can persist progress turn-by-turn rather than only at close.
   */
  onHistoryChange?: (turns: readonly ChatTurn[]) => void
  /**
   * A few things he might say, offered as tappable chips above the input
   * until the first message of this mount goes out — typed or tapped —
   * after which the row is gone for good, same as if he had just answered.
   */
  quickReplies?: readonly string[]
  /**
   * The zone's shape: this component fills whatever height its flex parent
   * gives it, and only the message list scrolls — the composer stays put at
   * the bottom. Off by default for the dev lab's bench card, which has no
   * such parent and wants the old self-sizing, capped-height behaviour.
   */
  fill?: boolean
  /** She ended it: the visible transcript should not be resumed next time. */
  onEnded?: () => void
}

interface Bubble extends ChatTurn {
  key: number
  /** True while an audio turn waits for its transcription. */
  pending?: boolean
  /** True for a turn seeded from an earlier mount today — plain text, no reveal. */
  seeded?: boolean
}

export function ChatThread({
  transport = apiChatTransport,
  onPose,
  opener,
  initialHistory,
  onHistoryChange,
  onEnded,
  quickReplies,
  fill = false,
}: ChatThreadProps) {
  const hasOpener = opener != null && opener.length > 0
  const seededTurns = initialHistory ?? []
  // Today's earlier turns, then her opener (spoken fresh now, so it still
  // gets the word-by-word reveal rather than joining the seeded, static ones).
  const initialTurns: ChatTurn[] = hasOpener
    ? [...seededTurns, { who: 'her', text: opener }]
    : [...seededTurns]
  const [bubbles, setBubbles] = useState<Bubble[]>(() =>
    initialTurns.map((turn, i) => ({ ...turn, key: i, seeded: i < seededTurns.length })),
  )
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState(false)
  const [chipsHidden, setChipsHidden] = useState(false)
  const nextKey = useRef(initialTurns.length)
  // However many of today's earlier turns were his, clamped to the live
  // endpoint's turn cap so the very next send is still a valid request.
  const seededUserTurns = seededTurns.filter((turn) => turn.who === 'user').length
  const userTurns = useRef(Math.min(seededUserTurns, TURN_MAX))
  const scroller = useRef<HTMLDivElement>(null)
  // The transcript the close call gets, unaffected by re-renders mid-flight —
  // seeded with today's earlier turns (if any) and her opener, when there is
  // one, before anything else is said.
  const record = useRef<ChatTurn[]>(initialTurns)

  const pose = (value: ChatPose) => onPose?.(value)

  // The session's one goodbye: whatever was said gets offered for extraction
  // exactly once, when the thread unmounts with the sheet. Both identities
  // are stable for the life of a session (the dev bench remounts by key when
  // it swaps transports), so this cleanup only ever runs on unmount.
  useEffect(() => {
    return () => {
      transport.close(record.current)
      onPose?.(null)
    }
  }, [onPose, transport.close])

  // Follow the conversation down as it grows.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bubbles is the trigger, not a read.
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight })
  }, [bubbles])

  const push = (bubble: Omit<Bubble, 'key'>): number => {
    const key = nextKey.current++
    setBubbles((current) => [...current, { ...bubble, key }])
    return key
  }

  const deliver = async (input: ChatSend, pendingKey: number | null) => {
    const history = record.current.slice(-HISTORY_MAX)
    userTurns.current += 1
    setSending(true)
    pose('thinking')
    try {
      const result = await transport.send(input, history, userTurns.current)
      const saidText = input.message ?? result.heard ?? '(balso žinutė)'
      record.current = [...record.current, { who: 'user', text: saidText }]
      onHistoryChange?.(record.current)
      if (pendingKey != null) {
        setBubbles((current) =>
          current.map((b) => (b.key === pendingKey ? { ...b, text: saidText, pending: false } : b)),
        )
      }
      // A fallback line is her excuse, not her turn — it stays out of the
      // record so the extraction never studies a conversation with a pool.
      if (!result.fallback) {
        record.current = [...record.current, { who: 'her', text: result.reply }]
        onHistoryChange?.(record.current)
      }
      push({ who: 'her', text: result.reply })
      playCue('reply')
      if (result.rude) {
        recordRudeExit()
        setDone(true)
        onEnded?.()
      } else if (result.endsConversation) {
        setDone(true)
        // Whatever the reason — her own goodbye, a spent budget, a door that
        // closed elsewhere — this conversation is over rather than paused.
        // Without saying so, reopening resumes the turn count and the prompt
        // asks her to say goodbye again, every time.
        onEnded?.()
      }
    } finally {
      setSending(false)
      pose(null)
    }
  }

  // Shared by the typed draft and a tapped quick-reply chip — either is him
  // saying something, so either retires the chip row for the rest of this mount.
  const sendMessage = (raw: string) => {
    const message = raw.trim().slice(0, MESSAGE_MAX)
    if (message.length === 0 || sending || done) return
    setChipsHidden(true)
    setDraft('')
    push({ who: 'user', text: message })
    void deliver({ message }, null)
  }

  const sendText = () => sendMessage(draft)

  const sendClip = (clip: VoiceClip) => {
    if (sending || done) return
    const key = push({ who: 'user', text: '(balso žinutė)', pending: true })
    void deliver({ audio: { mimeType: clip.mimeType, base64: clip.base64 } }, key)
  }

  // On-device Lithuanian speech lands in the draft for a look before sending;
  // the recorded fallback has nothing to show, so it sends on release.
  const speech = useSpeech('lt', (transcript) => {
    setDraft((current) => `${current} ${transcript}`.trim())
  })
  const recorder = useRecorder(sendClip)
  const speechOnDevice = onDeviceSpeech('lt')
  const micAvailable = speechOnDevice || recorderAvailable()
  const listening = speech.listening || recorder.listening

  const micDown = () => {
    if (sending || done) return
    pose('listening')
    if (speechOnDevice) speech.start()
    else recorder.start()
  }
  const micUp = () => {
    pose(null)
    if (speechOnDevice) speech.stop()
    else recorder.stop()
  }

  return (
    <div className={`flex w-full min-h-0 flex-col gap-3 ${fill ? 'h-full' : ''}`}>
      {(bubbles.length > 0 || fill) && (
        <div
          ref={scroller}
          className={`flex w-full min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain px-0.5 text-left ${
            fill ? 'flex-1' : 'max-h-[38dvh]'
          }`}
        >
          {bubbles.map((bubble) =>
            bubble.who === 'user' ? (
              <p
                key={bubble.key}
                className={`ml-8 self-end rounded-[var(--radius-card)] bg-raised px-3 py-2 text-ink text-sm ${bubble.pending === true ? 'opacity-60' : ''}`}
              >
                {bubble.text}
              </p>
            ) : (
              <p
                key={bubble.key}
                className="mr-8 self-start px-0.5 py-1 text-[0.9375rem] text-ink leading-[1.375rem]"
              >
                {bubble.seeded === true ? bubble.text : <GhostReveal>{bubble.text}</GhostReveal>}
              </p>
            ),
          )}
        </div>
      )}

      {quickReplies != null && quickReplies.length > 0 && !chipsHidden && (
        <div className="flex flex-wrap gap-2">
          {quickReplies.map((reply) => (
            <button
              key={reply}
              type="button"
              onClick={() => sendMessage(reply)}
              disabled={sending || done}
              className="rounded-full border border-[var(--her)]/40 px-3 py-1.5 text-[var(--her)] text-sm disabled:opacity-40"
            >
              {reply}
            </button>
          ))}
        </div>
      )}

      <div className="flex w-full items-end gap-2">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              sendText()
            }
          }}
          rows={1}
          maxLength={MESSAGE_MAX}
          disabled={done}
          placeholder={done ? 'Pokalbis baigtas.' : 'Parašyk jai…'}
          className="min-h-[2.5rem] flex-1 resize-none rounded-[var(--radius-control)] border border-line bg-surface px-3 py-2 text-ink text-sm placeholder:text-mute focus:border-[var(--her)] focus:outline-none disabled:opacity-50"
        />
        {micAvailable && !done && (
          <button
            type="button"
            onPointerDown={micDown}
            onPointerUp={micUp}
            onPointerLeave={() => listening && micUp()}
            onPointerCancel={micUp}
            aria-label="Kalbėti"
            className={`shrink-0 rounded-[var(--radius-control)] border px-3 py-2 text-sm ${
              listening
                ? 'border-[var(--her)] bg-[linear-gradient(var(--her-wash),var(--her-wash))] text-[var(--her)]'
                : 'border-line text-dim'
            }`}
          >
            {listening ? '● klausau' : 'balsu'}
          </button>
        )}
        <button
          type="button"
          onClick={sendText}
          disabled={sending || done || draft.trim().length === 0}
          className="shrink-0 rounded-[var(--radius-control)] border border-[var(--her)]/60 px-3 py-2 font-semibold text-[var(--her)] text-sm disabled:opacity-40"
        >
          {sending ? '…' : 'Siųsti'}
        </button>
      </div>
    </div>
  )
}
